import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Truck, CheckCircle2, AlertTriangle, MapPin, Clock, Phone, Ruler, ShieldCheck, ArrowRight, Droplets, Sprout, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
export function DeliveryPrep() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.5
    }
  };
  const stagger = {
    initial: {
      opacity: 0
    },
    whileInView: {
      opacity: 1
    },
    viewport: {
      once: true
    },
    transition: {
      staggerChildren: 0.1
    }
  };
  const item = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5
    }
  };
  return <div className="pb-16 bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-2520" data-mp="2q4zrz7hmwbif">
      {/* Hero Section — gravel photo background with a uniform black
         overlay, same treatment as the rest of the site's photo heroes
         so the white headline stays readable while the material under
         it cues "this page is about delivery prep, on real gravel." */}
      <section className="relative py-20 overflow-hidden border-b border-stone-200 dark:border-white/15" data-id="element-2521" data-mp="cgdj1t9w947k">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/gukm22ExLHvasq3sgFamZQ/PXL_20260530_192218218~2.jpg')"
      }} aria-hidden="true" data-id="element-2522" data-mp="196390l7bwxuh" />
        {/* Black overlay — matches the flat opacity Hero.tsx uses on
         mobile, since this page is always center-aligned and doesn't
         need a directional gradient. */}
        <div className="absolute inset-0 bg-black/60" aria-hidden="true" data-id="element-2523" data-mp="3kwfbnxsixgyr" />
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" data-id="element-2524" data-mp="3fvrppqj5jqbe" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2525" data-mp="3heroigifz8wk">
          
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1,
          duration: 0.5
        }} className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6" data-id="element-2526" data-mp="3mb8c99lqbl94">
            Prepare for Your <span className="text-brand-orange" data-id="element-2527" data-mp="2ykvxbes2go91">Delivery</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto font-medium" data-id="element-2528" data-mp="33f03hvtics92">
            Follow this simple guide to ensure a smooth, safe, and successful
            material delivery to your home or job site.
          </motion.p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16" data-id="element-2529" data-mp="212gtvk6bn4m4">
        {/* Before Delivery Day */}
        <motion.section {...fadeIn} data-id="element-2530" data-mp="1w3maag0j5c0y">
          <div className="flex items-center gap-4 mb-8" data-id="element-2531" data-mp="1w4nminta84k9">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/30 dark:border-white/15" data-id="element-2532" data-mp="2a2vh4p2uits9">
              <CalendarDays className="w-6 h-6 text-brand-orange" data-id="element-2533" data-mp="2k0060wo2t3cr" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-2534" data-mp="2jxx9ruwbydzl">
              Before Delivery Day
            </h2>
          </div>
          <motion.div variants={stagger} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2535" data-mp="2tpjmxws8an4z">
            {[{
            icon: <Truck className="w-5 h-5" data-id="element-2536" data-mp="32r13thdgmsuq" />,
            text: 'Clear the dump area of vehicles, toys, and obstacles'
          }, {
            icon: <MapPin className="w-5 h-5" data-id="element-2537" data-mp="w2ipag3df1wn" />,
            text: 'Mark the exact spot where you want material dumped (use a cone, tarp, or flag)'
          }, {
            icon: <ShieldCheck className="w-5 h-5" data-id="element-2538" data-mp="10zr72fklx3xd" />,
            text: 'Ensure gate access if fenced and provide gate codes if applicable'
          }, {
            icon: <Ruler className="w-5 h-5" data-id="element-2539" data-mp="19ufd47y0apn8" />,
            text: 'Measure clearance: Trucks are 10ft wide, 12ft tall, and 30ft long'
          }, {
            icon: <AlertTriangle className="w-5 h-5" data-id="element-2540" data-mp="d5hhknifmnp0" />,
            text: 'Check for low-hanging overhead wires or tree branches'
          }, {
            icon: <CheckCircle2 className="w-5 h-5" data-id="element-2541" data-mp="m3td93y5obob" />,
            text: 'Confirm someone 18+ will be on-site to receive the delivery'
          }].map((point, i) => <motion.div key={i} variants={item} className="flex items-start gap-3 p-4 bg-white dark:bg-[#282828] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-2542" data-mp="r4piply5o5vu" data-mpi={i}>
                <div className="text-brand-orange shrink-0 mt-0.5" data-id="element-2543" data-mp="22h9w10f6dgyl">
                  {point.icon}
                </div>
                <p className="text-brand-charcoal dark:text-stone-200 font-medium" data-id="element-2544" data-mp="2com554tk4yki">
                  {point.text}
                </p>
              </motion.div>)}
          </motion.div>
        </motion.section>

        {/* Day of Delivery */}
        <motion.section {...fadeIn} data-id="element-2545" data-mp="1yvmqf51ln8e0">
          <div className="flex items-center gap-4 mb-8" data-id="element-2546" data-mp="1yulfr1ohwnwt">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20 dark:border-white/15" data-id="element-2547" data-mp="1twjklidsoy4z">
              <Clock className="w-6 h-6 text-brand-orange" data-id="element-2548" data-mp="1ovm4rcg9cg6n" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-2549" data-mp="1jylzh3r8fhgl">
              Day of Delivery
            </h2>
          </div>
          <div className="bg-white dark:bg-[#282828] rounded-2xl border border-stone-100 dark:border-white/10 shadow-sm overflow-hidden" data-id="element-2550" data-mp="3ecdg4n7zb1lj">
            <div className="divide-y divide-stone-100 dark:divide-white/5" data-id="element-2551" data-mp="3aarvgfp7dj0s">
              {[{
              icon: <Phone className="w-6 h-6" data-id="element-2552" data-mp="17m1r1vk5o6a6" />,
              title: 'Driver will call',
              desc: 'Expect a call 15-30 minutes before the driver arrives at your location.'
            }, {
              icon: <MapPin className="w-6 h-6" data-id="element-2553" data-mp="yohpelids7z1" />,
              title: 'Point the way',
              desc: 'Direct the driver to your marked dump spot upon arrival.'
            }, {
              icon: <AlertTriangle className="w-6 h-6" data-id="element-2554" data-mp="1qfe0bgkvs2jo" />,
              title: 'Stay clear',
              desc: 'Keep children, pets, and yourself at a safe distance during the dumping process.'
            }, {
              icon: <CheckCircle2 className="w-6 h-6" data-id="element-2555" data-mp="1hcwc4wlo0zu1" />,
              title: 'Inspect material',
              desc: 'Verify the material is correct before the driver leaves the site.'
            }].map((step, i) => <div key={i} className="p-6 flex items-start gap-4" data-id="element-2556" data-mp="5wo8eruhzigs" data-mpi={i}>
                  <div className="w-10 h-10 rounded-full bg-brand-orange/10 dark:bg-brand-orange/20 flex items-center justify-center text-brand-orange shrink-0" data-id="element-2557" data-mp="axlyrrpnvitd">
                    {step.icon}
                  </div>
                  <div data-id="element-2558" data-mp="3ujcirjplmsz9">
                    <h3 className="text-lg font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-2559" data-mp="3um71n6sys5qk">
                      {step.title}
                    </h3>
                    <p className="text-stone-700 dark:text-stone-400 font-medium" data-id="element-2560" data-mp="7f50kx9l7z38">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.section>

        {/* Access Tips */}
        <motion.section {...fadeIn} data-id="element-2561" data-mp="3pl1ahdeycqj5">
          <div className="flex items-center gap-4 mb-8" data-id="element-2562" data-mp="3pm2sy2yasfaw">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20 dark:border-white/15" data-id="element-2563" data-mp="3kub3jvqvofmc">
              <Truck className="w-6 h-6 text-brand-orange" data-id="element-2564" data-mp="2x35mxed45jaz" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-2565" data-mp="2s4m0lculy5i3">
              Access Tips
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2566" data-mp="2blfa9wqqxuv5">
            <div className="p-6 bg-white dark:bg-[#262626] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-2567" data-mp="2blnn5omvd06c">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-2568" data-mp="2gmurtetptj8s">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-2569" data-mp="2pl6i1fqo6f0p" />
                Firm Ground Required
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-2570" data-mp="2ujhqpn58ajhc">
                Fully loaded trucks are extremely heavy. They need firm ground
                or pavement to drive on, not soggy lawns or soft dirt, to avoid
                getting stuck or causing damage.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#262626] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-2571" data-mp="39aqowkn75d63">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-2572" data-mp="3d8emjvg9xk1o">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-2573" data-mp="xlty1p4h3q46" />
                Steep Driveways
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-2574" data-mp="sm7k1dgtm5ub">
                Very steep driveways may be unsafe for a dump truck to navigate
                or dump on. In these cases, a street-level dump may be required.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#262626] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-2575" data-mp="1gcbmhhi9vzjq">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-2576" data-mp="1bktnv03q2b1x">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-2577" data-mp="16lh1eggrj5zv" />
                Cul-de-sacs & Tight Turns
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-2578" data-mp="2437b65rfmemr">
                Large dump trucks need significant turning radius. Ensure there
                is adequate room to maneuver in cul-de-sacs or tight
                neighborhoods.
              </p>
            </div>
            <div className="p-6 bg-brand-orange/10 dark:bg-brand-orange/10 rounded-xl border border-brand-orange/30 dark:border-white/0 shadow-sm flex flex-col justify-center" data-id="element-2579" data-mp="7zdt8jq0epcz">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-2580" data-mp="huxp564kpo1q">
                <Phone className="w-5 h-5 text-brand-orange" data-id="element-2581" data-mp="vtxd0bk9hywu" />
                Unsure about access?
              </h3>
              <p className="text-stone-700 dark:text-stone-300 text-sm font-medium mb-3" data-id="element-2582" data-mp="quce2153dqk4">
                If you have concerns about your delivery location, call us first
                and we can review a photo of your site.
              </p>
              <a href="tel:206-619-3742" className="inline-flex items-center text-brand-orange font-bold hover:underline" data-id="element-2583" data-mp="1elhtwgb18iuf">
                Call (206) 619-3742 <ArrowRight className="w-4 h-4 ml-1" data-id="element-2584" data-mp="1am8zrac2yiyc" />
              </a>
            </div>
          </div>
        </motion.section>

        {/* After Delivery */}
        <motion.section {...fadeIn} data-id="element-2585" data-mp="1y81gsbpup3h4">
          <div className="flex items-center gap-4 mb-8" data-id="element-2586" data-mp="1y70648cqyizx">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20 dark:border-white/15" data-id="element-2587" data-mp="1k8tf9c67tj9r">
              <Sprout className="w-6 h-6 text-brand-orange" data-id="element-2588" data-mp="1p97ogxjjyz9d" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-2589" data-mp="1agnr6dj33lzu">
              After Delivery
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2590" data-mp="11ibp9fcrp1s3">
            <div className="p-6 bg-white dark:bg-[#282828] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm text-center" data-id="element-2591" data-mp="11fpllqszz6f5">
              <Clock className="w-8 h-8 text-brand-orange mx-auto mb-3" data-id="element-2592" data-mp="rj31j48hwje6" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-2593" data-mp="wi63ugx3cbig">
                Spread Quickly
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-2594" data-mp="2y8n1n3x88f88">
                Spread material within 48 hours to avoid settling and compaction
                in the pile.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#282828] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm text-center" data-id="element-2595" data-mp="2pfjlgnv75o9w">
              <Droplets className="w-8 h-8 text-brand-orange mx-auto mb-3" data-id="element-2596" data-mp="3h67lfwl4wnlz" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-2597" data-mp="3h44tv84c2mf5">
                Wet Before Compacting
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-2598" data-mp="384zw440lix41">
                Lightly wet gravel before using a plate compactor for maximum
                density and stability.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#282828] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm text-center" data-id="element-2599" data-mp="1wrxhvflyyjn1">
              <Sprout className="w-8 h-8 text-brand-orange mx-auto mb-3" data-id="element-2600" data-mp="1hnuoscsnvmaf" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-2601" data-mp="1hlrn2vi5c4pv">
                Protect Trees
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-2602" data-mp="1rl8qf69xe6io">
                When spreading bark or mulch, keep it at least 3 inches away
                from tree trunks to prevent rot.
              </p>
            </div>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section {...fadeIn} className="bg-brand-charcoal dark:bg-[#282828] rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-2603" data-mp="25cdwor7stx1z">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none" data-id="element-2604" data-mp="2ad1o3dzxwfwq"></div>
          <div className="relative z-10" data-id="element-2605" data-mp="fzaegehm1w1a">
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white mb-4" data-id="element-2606" data-mp="g0limfeyx5sx">
              Ready to order?
            </h2>
            <p className="text-stone-300 text-lg mb-8 max-w-xl mx-auto font-medium" data-id="element-2607" data-mp="2bdnyyjz4h4iy">
              Now that you know how to prepare, head over to our Materials Hub
              to select your products and schedule your delivery.
            </p>
            <a href="/#materials" className="inline-flex items-center justify-center bg-brand-orange hover:bg-brand-orangeHover text-white px-8 py-4 rounded-xl font-extrabold text-lg transition-all shadow-[0_0_15px_rgba(255,140,50,0.3)] hover:shadow-[0_0_20px_rgba(255,140,50,0.3)]" data-id="element-2608" data-mp="2keca96x4sqs5">
              Browse Materials Hub
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-2609" data-mp="21g9hqi5dkbpl" />
            </a>
          </div>
        </motion.section>
      </div>
    </div>;
}